import React from "react";
import { AddActionButton } from "../AddActionButton";
import { ValueMultiSelect } from "../ValueMultiSelect";
import { ValuePicker } from "../ValuePicker";
import {
    RULE_OPERATORS,
    appendRuleNode,
    canNestRuleGroupNodes,
    ruleOperatorLabel,
    ruleOpDef,
    columnDataTypeCategory,
    operatorsForColumn,
    isOperatorAllowedForColumn,
    operatorForColumnChange,
    columnMatchesKey,
    dataColumnSelectionKey,
    findRuleNode,
    isRuleGroupNode,
    makeRuleCondition,
    makeRuleConditionGroup,
    mapRuleNodes,
    nextRuleJoinForGroup,
    ruleGroupChildren,
    ruleLeafConditions,
    ruleNodeIds,
    ruleNodeJoin,
    ruleNodesHaveMixedJoins,
    ruleNodesUniformJoin,
    setRuleNodesUniformJoin,
    comparableColumnsForColumn,
    differenceColumnsForColumn,
    columnPickerLabel,
    COLUMN_COMPARISON_OPERATORS,
    MINUTES_BETWEEN_OPERATORS,
} from "../../reportConfigurationLogic";
import {
    availableDifferenceUnits,
    resolveDifferenceUnit,
    effectiveComparisonTier,
    temporalTier,
    isTemporalColumn,
} from "../../logic/temporalPrecision";
import {
    DEFAULT_BLANK_DATE,
    TODAY_COMPARISON_OPERATORS,
    conditionBlankValue,
    isDateLikeColumn,
    nullFallbackFor,
    nullFallbackIssue,
    nullFallbackSupported,
    nullFallbackType,
} from "../../logic/sqlExpressions";
import type { DataColumn, ReportConfig, ReportConfigurationLookupOption, RuleCondition, RuleConditionGroup, RuleNode, RuleOperatorKey } from "../../types";
import { ReportDatePicker } from "../ReportDatePicker";
import { IconAction, StatusIcon } from "./EditorControls";

type ConditionMode = "value" | "column" | "today" | "minutes";

const GRANULARITY_OPTIONS = [
    { id: "auto", value: "auto", name: "Automatic (match the less precise column)" },
    { id: "day", value: "day", name: "Day" },
    { id: "minute", value: "minute", name: "Minute" },
    { id: "second", value: "second", name: "Second" },
    { id: "exact", value: "exact", name: "Exact (no rounding)" },
];

const UNIT_OPTIONS = [
    { id: "minute", value: "minute", name: "Minutes" },
    { id: "hour", value: "hour", name: "Hours" },
    { id: "day", value: "day", name: "Days" },
];

const TIER_NAME: Record<string, string> = { day: "day", minute: "minute", second: "second" };

export const RULE_LOGIC_OPTIONS = [
    { id: "and", value: "and", name: "All conditions match (AND)" },
    { id: "or", value: "or", name: "Any condition matches (OR)" },
];

export interface RuleTreeCtx {
        scope: ReportConfig;
        columns: DataColumn[];
        nodes: RuleNode[];
        logic: "and" | "or";
        updateNodes: (updater: (nodes: RuleNode[]) => RuleNode[]) => void;
        setRootLogic: (logic: "and" | "or") => void;
        /**
         * Whether the operator list is narrowed to what the selected column's data type
         * supports. On for the metric filters, off for Categorization - see the note on
         * operatorsForColumn for why the two differ.
         */
        restrictOperatorsByType: boolean;
        /**
         * Timestamp columns a "minutes between" condition may use. Empty (or omitted) hides
         * that subject mode entirely, which is what keeps the condition row unchanged for
         * any builder whose model has no comparable timestamps.
         */
        timeColumns?: DataColumn[];
    }

export function updateRuleTree(ctx: RuleTreeCtx, updater: (nodes: RuleNode[]) => RuleNode[]) {
        ctx.updateNodes(updater);
    }

export function updateRuleGroupNode(ctx: RuleTreeCtx, groupId: string, updater: (group: RuleConditionGroup) => RuleConditionGroup) {
        updateRuleTree(ctx, (nodes) => mapRuleNodes(nodes, groupId, (node) => isRuleGroupNode(node) ? updater(node) : node));
    }

export function setRuleLevelUniformJoin(ctx: RuleTreeCtx, groupId: string, logic: "and" | "or") {
        if (!groupId) {
            const rewritten = setRuleNodesUniformJoin({ conditions: ctx.nodes, conditionLogic: ctx.logic }, logic);
            ctx.updateNodes(() => rewritten.conditions);
            ctx.setRootLogic(logic);
            return;
        }
        updateRuleGroupNode(ctx, groupId, (group) => setRuleNodesUniformJoin(group, logic));
    }

export function ruleLevelLogicPlaceholder(nodes: RuleNode[] | null | undefined, logic: "and" | "or") {
        const uniform = ruleNodesUniformJoin(nodes, logic);
        if (!uniform) return "Mixed AND/OR - set all...";
        return (RULE_LOGIC_OPTIONS.find((option) => option.value === uniform) || RULE_LOGIC_OPTIONS[0]).name;
    }

type RuleConditionEditorProps = {
    ctx: RuleTreeCtx;
    groupId: string;
    nodes: RuleNode[];
    logic: "and" | "or";
    depth: number;
    reserveUniqueId: (prefix: string, usedIds: Set<string>) => string;
    lookupOptionsForReportColumn: (report: ReportConfig, columnKey: string) => ReportConfigurationLookupOption[];
    lookupLabelForReportColumn: (report: ReportConfig, columnKey: string, value: string) => string;
};

export function RuleConditionEditor({ ctx, groupId, nodes, logic, depth, reserveUniqueId, lookupOptionsForReportColumn, lookupLabelForReportColumn }: RuleConditionEditorProps) {

    /**
     * The operator a node appended to `groupId` should join with: whatever that level's
     * last existing junction uses, so appending to an all-OR level gives OR instead of
     * reverting to the level's stored default.
     */
    function inheritedJoinFor(nodes: RuleNode[], groupId: string, fallbackLogic: "and" | "or") {
        const siblings = groupId ? ruleGroupChildren(findRuleNode(nodes, groupId)) : nodes;
        return nextRuleJoinForGroup(siblings, fallbackLogic);
    }

    /** Adds a leaf condition to the group with id groupId; "" targets the tree's root group. */
    function addRuleCondition(ctx: RuleTreeCtx, groupId: string, levelLogic: "and" | "or") {
        const usedIds = ruleNodeIds(ctx.nodes);
        const condition = makeRuleCondition();
        condition.id = reserveUniqueId("cond", usedIds);
        condition.joinOperator = inheritedJoinFor(ctx.nodes, groupId, levelLogic);
        updateRuleTree(ctx, (nodes) => appendRuleNode(nodes, groupId, condition));
    }

    /**
     * Adds a nested group to the group with id groupId ("" = root). Its internal operator
     * starts as the opposite of the level it sits in, since a group that repeats its
     * parent's operator would not change the meaning of the expression. Its junction to the
     * previous sibling is inherited like any other appended node, and both are editable.
     */
    function addRuleGroup(ctx: RuleTreeCtx, groupId: string, levelLogic: "and" | "or") {
        const usedIds = ruleNodeIds(ctx.nodes);
        const group = makeRuleConditionGroup(levelLogic === "and" ? "or" : "and");
        group.id = reserveUniqueId("grp", usedIds);
        group.joinOperator = inheritedJoinFor(ctx.nodes, groupId, levelLogic);
        group.conditions = group.conditions.map((child) => ({ ...child, id: reserveUniqueId("cond", usedIds) }));
        updateRuleTree(ctx, (nodes) => appendRuleNode(nodes, groupId, group));
    }

    /** Sets the operator joining one node to its previous sibling. */
    function setRuleNodeJoin(ctx: RuleTreeCtx, nodeId: string, join: "and" | "or") {
        updateRuleTree(ctx, (nodes) => mapRuleNodes(nodes, nodeId, (node) => ({ ...node, joinOperator: join })));
    }

    /** Removes one node (a condition, or a group together with everything inside it). */
    function removeRuleNode(ctx: RuleTreeCtx, nodeId: string) {
        updateRuleTree(ctx, (nodes) => mapRuleNodes(nodes, nodeId, () => null));
    }

    /**
     * The AND/OR toggle sitting between two members of the same level. Each junction is
     * independent, so one level can read "(A AND B) OR C OR D AND (E AND F)". AND binds
     * tighter than OR - the outline above the rule shows the resulting grouping.
     */
    function renderRuleConnector(ctx: RuleTreeCtx, node: RuleNode, levelLogic: "and" | "or") {
        const join = ruleNodeJoin(node, levelLogic);
        return (
            <div className="rc-rule-connector">
                <div className={`rc-connector-toggle selected-${join}`} role="group" aria-label="Combine with the condition above">
                    {(["and", "or"] as const).map((option) => (
                        <button
                            key={option}
                            type="button"
                            data-op={option}
                            className={join === option ? "rc-connector-option active" : "rc-connector-option"}
                            aria-pressed={join === option}
                            onClick={() => setRuleNodeJoin(ctx, node.id, option)}
                        >
                            <span className="material-icons md-14 rc-connector-check" aria-hidden="true">check</span>
                            {option.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    /**
     * Renders the members of one group plus its Add Condition / Add Group actions.
     * `groupId` is "" for the rule's own root group. `logic` is the level's default
     * operator, used for any member without its own `joinOperator`.
     */
    function renderRuleGroupBody(ctx: RuleTreeCtx, groupId: string, nodes: RuleNode[], logic: "and" | "or", depth: number) {
        const canAddGroup = canNestRuleGroupNodes(ctx.nodes, groupId);
        return (
            <>
                {nodes.length === 0 && (
                    <p className="rc-hint indent">
                        {groupId
                            ? "This group is empty, so it matches nothing. Add a condition inside it, or delete the group."
                            : "No conditions yet. This rule will not generate SQL until a condition is added."}
                    </p>
                )}
                {nodes.map((node, nodeIndex) => (
                    <React.Fragment key={node.id}>
                        {nodeIndex > 0 && renderRuleConnector(ctx, node, logic)}
                        {isRuleGroupNode(node)
                            ? renderRuleGroup(ctx, node, depth + 1)
                            : renderCondition(ctx, node as RuleCondition)}
                    </React.Fragment>
                ))}
                {ruleNodesHaveMixedJoins(nodes, logic) && (
                    <p className="rc-hint indent rc-precedence-note">
                        This level mixes AND and OR. AND is applied first, so AND-joined conditions group together before the ORs, exactly as in SQL.
                    </p>
                )}
                <div className="rc-rule-node-actions indent">
                    <AddActionButton className="compact" onClick={() => addRuleCondition(ctx, groupId, logic)}>Add Condition</AddActionButton>
                    {canAddGroup
                        ? <AddActionButton className="compact" onClick={() => addRuleGroup(ctx, groupId, logic)}>Add Group</AddActionButton>
                        : <span className="rc-hint">Maximum nesting depth reached.</span>}
                </div>
            </>
        );
    }

    function ruleGroupLegend(nodes: RuleNode[] | null | undefined, logic: "and" | "or") {
        const leafCount = ruleLeafConditions(nodes || []).length;
        const uniform = ruleNodesUniformJoin(nodes, logic);
        if (!leafCount) return { tone: "empty", label: "Empty group", count: "No conditions" };
        if (!uniform) return { tone: "mixed", label: "Mixed logic", count: `${leafCount} condition${leafCount === 1 ? "" : "s"}` };
        return {
            tone: uniform,
            label: uniform === "and" ? "All conditions" : "Any condition",
            count: `${leafCount} condition${leafCount === 1 ? "" : "s"}`,
        };
    }

    /**
     * One nested group: a bulk AND/OR setter for its own junctions, a delete action, and its
     * recursive body. The dropdown is a convenience - the authoritative operators are the
     * per-junction connectors inside.
     */
    function renderRuleGroup(ctx: RuleTreeCtx, group: RuleConditionGroup, depth: number) {
        const children = group.conditions || [];
        const leafCount = ruleLeafConditions(children).length;
        const legend = ruleGroupLegend(children, group.conditionLogic);
        return (
            <div className={`rc-rule-group depth-${Math.min(depth, 5)}`} key={group.id}>
                <div className="rc-rule-row rc-rule-group-head">
                    <div className="rc-rule-group-legend" aria-label={`${legend.label}, ${legend.count}`}>
                        <span className="material-icons md-16" aria-hidden="true">account_tree</span>
                        <span className="rc-rule-group-name">Group</span>
                        <span className={`rc-rule-logic-chip ${legend.tone}`}>{legend.label}</span>
                        <span className="rc-rule-count-chip">{legend.count}</span>
                    </div>
                    {children.length > 1 && (
                        <ValuePicker
                            mode="select"
                            options={RULE_LOGIC_OPTIONS}
                            placeholder={ruleLevelLogicPlaceholder(children, group.conditionLogic)}
                            searchPlaceholder="Search Logic..."
                            onPick={(value) => setRuleLevelUniformJoin(ctx, group.id, value as "and" | "or")}
                        />
                    )}
                    <span className="rc-spacer" />
                    <StatusIcon ready={leafCount > 0} />
                    <IconAction
                        label={leafCount ? `Delete Group And Its ${leafCount} Condition${leafCount === 1 ? "" : "s"}` : "Delete Group"}
                        onClick={() => removeRuleNode(ctx, group.id)}
                    />
                </div>
                <div className="rc-rule-group-body">
                    {renderRuleGroupBody(ctx, group.id, children, group.conditionLogic, depth)}
                </div>
            </div>
        );
    }

    /**
     * Switches a condition between its operand modes, clearing whatever belonged to the old
     * one. Values and the second column are dropped together because neither survives the
     * change in meaning, and the operator is reset to the first one the new mode allows so a
     * condition is never left displaying an operator its mode cannot generate SQL for.
     */
    function setConditionMode(ctx: RuleTreeCtx, condition: RuleCondition, mode: ConditionMode) {
        updateCondition(ctx, condition.id, (current) => {
            const base = { ...current, values: [], text: "", compareColumn: "", absoluteMinutes: false };
            if (mode === "minutes") {
                return { ...base, leftKind: "minutes_between" as const, compareTo: "value" as const, column: "", operator: "gt" as RuleOperatorKey, blankDate: undefined, blankValue: undefined };
            }
            if (mode === "today") {
                // Only a date column can be compared to today; any other column is cleared.
                const currentColumn = ctx.columns.find((column) => columnMatchesKey(column, current.column));
                const column = isDateLikeColumn(currentColumn) ? current.column : "";
                return { ...base, leftKind: "column" as const, compareTo: "today" as const, column, operator: "lte" as RuleOperatorKey, blankDate: column ? current.blankDate : undefined, blankValue: column ? current.blankValue : undefined };
            }
            if (mode === "column") {
                return { ...base, leftKind: "column" as const, compareTo: "column" as const, operator: "eq" as RuleOperatorKey };
            }
            return { ...base, leftKind: "column" as const, compareTo: "value" as const, operator: "in" as RuleOperatorKey };
        });
    }

    function conditionMode(condition: RuleCondition): ConditionMode {
        if (condition.leftKind === "minutes_between") return "minutes";
        if (condition.compareTo === "today") return "today";
        return condition.compareTo === "column" ? "column" : "value";
    }

    function renderCondition(ctx: RuleTreeCtx, condition: RuleCondition) {
        const report = ctx.scope;
        const columns = ctx.columns;
        const mode = conditionMode(condition);
        const timeColumns = ctx.timeColumns || [];
        const dateColumns = columns.filter((column) => isDateLikeColumn(column));
        const selectedColumn = mode === "minutes"
            ? timeColumns.find((column) => columnMatchesKey(column, condition.column))
            : columns.find((column) => columnMatchesKey(column, condition.column));
        // Where the ctx restricts by type, only the operators this column's data type supports
        // are offered; an operator saved before the column was classified can therefore be
        // absent from the list, and is surfaced through the placeholder below rather than the
        // dropdown silently displaying someone else's operator. Categorization does not
        // restrict, so its list is unchanged from before.
        // Operator narrowing is per CONDITION, not per builder. Categorization deliberately
        // offers every operator on every column (its pool is chosen by ColumnRole, not by
        // type), and that must not change - but a "minutes between" subject is unambiguously
        // numeric, and a comparison against a column has no reading for the list, text and
        // blank operators. So the two new modes narrow while the ordinary mode keeps the
        // builder's existing behaviour exactly.
        const modeOperators = mode === "minutes"
            ? RULE_OPERATORS.filter((item) => MINUTES_BETWEEN_OPERATORS.includes(item.key))
            : mode === "column"
                ? RULE_OPERATORS.filter((item) => COLUMN_COMPARISON_OPERATORS.includes(item.key))
                : mode === "today"
                    ? RULE_OPERATORS.filter((item) => TODAY_COMPARISON_OPERATORS.includes(item.key))
                    : null;
        const availableOperators = modeOperators
            || (ctx.restrictOperatorsByType ? operatorsForColumn(selectedColumn) : RULE_OPERATORS);
        const operator = ruleOpDef(condition.operator);
        const operatorAllowed = modeOperators
            ? modeOperators.some((item) => item.key === condition.operator)
            : !ctx.restrictOperatorsByType || !condition.column || isOperatorAllowedForColumn(selectedColumn, condition.operator);
        // Labels still come from the RuleOperator lookup where it defines one, so renaming an
        // operator in the lookup keeps changing what both builders display.
        const operatorLabel = (key: RuleOperatorKey) => ruleOperatorLabel(key);
        const options = lookupOptionsForReportColumn(report, condition.column);
        const values = options.map((option) => option.value);
        // Calculated fields are listed as "<name> (Calculated)" (logic/calculatedColumns.ts).
        const toOptions = (list: DataColumn[]) => list.map((column) => ({ id: dataColumnSelectionKey(column) || column.key, value: dataColumnSelectionKey(column) || column.key, name: columnPickerLabel(column) }));
        const columnOptions = toOptions(mode === "minutes" ? timeColumns : mode === "today" ? dateColumns : columns);
        // The right-hand pool is resolved from the LEFT column, so it is same-model and
        // same-data-type; for a time difference the pool is the comparable timestamps.
        const compareColumns = mode === "minutes"
            ? (condition.column ? differenceColumnsForColumn(report, condition.column) : timeColumns)
            : condition.column ? comparableColumnsForColumn(report, condition.column) : [];
        const compareOptions = toOptions(compareColumns);
        const selectedCompareColumn = compareColumns.find((column) => columnMatchesKey(column, condition.compareColumn || ""));
        const setCompareColumn = (value: string) => updateCondition(ctx, condition.id, (current) => ({ ...current, compareColumn: value }));

        // --- Precision read-back ---------------------------------------------------
        // The default is automatic and usually invisible, so the hint has to say what it
        // actually resolved to - otherwise a comparison quietly rounded to the day looks
        // identical to one compared exactly, which is the trap this whole control exists to
        // close.
        const granularityLabel = GRANULARITY_OPTIONS.find((option) => option.value === (condition.compareGranularity || "auto"))?.name || "Automatic";
        const autoTier = effectiveComparisonTier(selectedColumn, selectedCompareColumn, null);
        const granularityHint = condition.compareGranularity
            ? condition.compareGranularity === "exact"
                ? "Compared at full precision. A date-only column counts as midnight, so this can match nothing."
                : `Both sides are rounded down to the ${TIER_NAME[condition.compareGranularity] || condition.compareGranularity} before comparing.`
            : autoTier
                ? `${selectedColumn?.label || "This column"} and ${selectedCompareColumn?.label || "the other column"} differ in precision, so both are compared by ${TIER_NAME[autoTier]}.`
                : "Both columns hold the same precision, so they are compared exactly as stored.";

        const unitsAvailable = availableDifferenceUnits(selectedColumn, selectedCompareColumn);
        const unitOptions = UNIT_OPTIONS.filter((option) => unitsAvailable.includes(option.value as "minute" | "hour" | "day"));
        const resolvedUnit = resolveDifferenceUnit(selectedColumn, selectedCompareColumn, condition.differenceUnit);
        const unitLabel = UNIT_OPTIONS.find((option) => option.value === resolvedUnit)?.name || "Minutes";
        const dayOnly = unitsAvailable.length === 1 && unitsAvailable[0] === "day";
        const unitHint = dayOnly
            ? `${!temporalTier(selectedColumn) || temporalTier(selectedColumn) === "day" ? (selectedColumn?.label || "One column") : (selectedCompareColumn?.label || "One column")} has no time of day, so this difference can only be counted in whole days.`
            : "Counted in whole units, the same way on-time performance measures lateness, so a difference of just under one unit can read as 0 or 1.";
        const operatorOptions = availableOperators.map((item) => ({ id: item.key, value: item.key, name: operatorLabel(item.key) }));
        // Range bounds are addressed by index, so the array is padded rather than left sparse:
        // typing the upper bound first would otherwise persist values as [null, "5"].
        const setValueAt = (index: number, value: string) => updateCondition(ctx, condition.id, (current) => {
            const next = [...current.values];
            while (next.length <= index) next.push("");
            next[index] = value;
            return { ...current, values: next };
        });
        const modeOptions = [
            { id: "value", value: "value", name: "Compare To A Value" },
            { id: "column", value: "column", name: "Compare To Another Column" },
            ...(dateColumns.length ? [{ id: "today", value: "today", name: "Compare To Today" }] : []),
            ...(timeColumns.length >= 2 ? [{ id: "minutes", value: "minutes", name: "Minutes Between Two Times" }] : []),
        ];
        const modeLabel = modeOptions.find((option) => option.value === mode)?.name || "Compare To A Value";
        return (
            <div className="rc-condition" key={condition.id}>
                <div className={mode === "minutes" ? "rc-condition-row rc-condition-mode rc-condition-mode-minutes" : "rc-condition-row rc-condition-mode"}>
                    <span className="rc-lbl">Compare</span>
                    <ValuePicker
                        mode="select"
                        options={modeOptions}
                        placeholder={modeLabel}
                        searchPlaceholder="Search Comparison Types..."
                        onPick={(value) => setConditionMode(ctx, condition, value as ConditionMode)}
                    />
                </div>
                <div className={mode === "minutes" ? "rc-condition-row rc-condition-top rc-condition-top-minutes" : "rc-condition-row rc-condition-top"}>
                    {/* .rc-condition-top is a THREE-column grid. A time difference needs two
                        column pickers, so its pair is wrapped in a single cell rather than
                        added as extra children - which would spill onto further grid rows and
                        misalign the operator and the remove button. */}
                    {mode === "minutes" && <div className="rc-minutes-subject">
                    <label><span>From</span>
                    <ValuePicker
                        mode="select"
                        options={columnOptions}
                        placeholder={selectedColumn?.label || "Choose A Time Column..."}
                        searchPlaceholder="Search Columns..."
                        onPick={(value) => updateCondition(ctx, condition.id, (current) => {
                            const nextColumn = columns.find((column) => columnMatchesKey(column, value));
                            const operator = modeOperators
                                ? current.operator
                                : ctx.restrictOperatorsByType ? operatorForColumnChange(nextColumn, current.operator) : current.operator;
                            return { ...current, column: value, operator, values: [], text: "" };
                        })}
                    />
                    </label>
                    <label><span>To</span>
                    <ValuePicker
                        mode="select"
                        options={compareOptions}
                        placeholder={selectedCompareColumn?.label || "Choose A Time Column..."}
                        searchPlaceholder="Search Time Columns..."
                        onPick={setCompareColumn}
                    />
                    </label>
                    </div>}
                    {mode !== "minutes" && <ValuePicker
                        mode="select"
                        options={columnOptions}
                        placeholder={(selectedColumn && columnPickerLabel(selectedColumn)) || "Choose A Column..."}
                        searchPlaceholder="Search Columns..."
                        onPick={(value) => updateCondition(ctx, condition.id, (current) => {
                            const nextColumn = columns.find((column) => columnMatchesKey(column, value));
                            // Values and text are cleared because they belonged to the old
                            // column. The operator is only rewritten where this ctx enforces
                            // types, and even then only when the new column cannot support it -
                            // so Categorization keeps its previous behaviour of never touching
                            // the operator on a column change.
                            // In a mode-narrowed condition the operator is already valid for
                            // the whole mode, so a column change must not rewrite it.
                            const operator = modeOperators
                                ? current.operator
                                : ctx.restrictOperatorsByType ? operatorForColumnChange(nextColumn, current.operator) : current.operator;
                            // Changing the left column invalidates a right column chosen from
                            // the old column's compatible pool, so it is cleared with the values.
                            const compareColumn = mode === "column" ? "" : current.compareColumn;
                            // An "If blank, use" date only means something on a date column.
                            // An "If blank, use" value belonged to the old column, like the values: it is
                            // kept only from one date column to another, where it still fits.
                            const previousColumn = columns.find((column) => columnMatchesKey(column, current.column));
                            const keepFallback = nullFallbackType(previousColumn).kind === "date" && nullFallbackType(nextColumn).kind === "date";
                            return {
                                ...current, column: value, operator, values: [], text: "", compareColumn,
                                blankDate: keepFallback ? current.blankDate : undefined,
                                blankValue: keepFallback ? current.blankValue : undefined,
                            };
                        })}
                    />}
                    <ValuePicker
                        mode="select"
                        options={operatorOptions}
                        placeholder={operatorAllowed ? operatorLabel(condition.operator) : `${operatorLabel(condition.operator)} (not valid here)`}
                        searchPlaceholder="Search Operators..."
                        onPick={(value) => updateCondition(ctx, condition.id, (current) => ({ ...current, operator: value as RuleOperatorKey, values: [], text: "" }))}
                    />
                    <IconAction label="Remove Condition" icon="remove_circle_outline" onClick={() => removeRuleNode(ctx, condition.id)} />
                </div>
                {mode === "column" && condition.column && (
                    <div className="rc-condition-values">
                        <ValuePicker
                            mode="select"
                            options={compareOptions}
                            placeholder={selectedCompareColumn?.label || (compareOptions.length ? "Choose A Column To Compare With..." : "No Comparable Columns On This Model")}
                            searchPlaceholder="Search Columns..."
                            onPick={setCompareColumn}
                        />
                        {isTemporalColumn(selectedColumn) && selectedCompareColumn && (
                            <div className="rc-granularity">
                                <span className="rc-lbl">Compare by</span>
                                <ValuePicker
                                    mode="select"
                                    options={GRANULARITY_OPTIONS}
                                    placeholder={granularityLabel}
                                    searchPlaceholder="Search Granularity..."
                                    onPick={(value) => updateCondition(ctx, condition.id, (current) => ({
                                        ...current,
                                        compareGranularity: value === "auto" ? undefined : value as "day" | "minute" | "second" | "exact",
                                    }))}
                                />
                                <p className="rc-hint">{granularityHint}</p>
                            </div>
                        )}
                        <p className="rc-hint">Only columns on the same data model that hold the same kind of value are offered, because comparing different kinds never matches.</p>
                    </div>
                )}
                {mode === "today" && condition.column && (
                    <div className="rc-condition-values">
                        <p className="rc-hint">Compared with the date the report runs (today), by day - a date-and-time column counts the whole of today.</p>
                    </div>
                )}
                {renderNullFallback(ctx, condition, selectedColumn)}
                {mode === "minutes" && (
                    <div className="rc-condition-values rc-minutes-values">
                        {/* Amount and unit belong on one line - they are a single quantity.
                            The toggle and the hint each get their own, so nothing has to wrap
                            around a neighbour of a different height. */}
                        <div className="rc-minutes-fields">
                            {operator.valueMode === "range" ? (
                                <>
                                    <label><span>At least</span><input className="rc-custom-value" placeholder="e.g. 15" value={condition.values[0] || ""} onChange={(event) => setValueAt(0, event.target.value)} /></label>
                                    <label><span>At most</span><input className="rc-custom-value" placeholder="e.g. 45" value={condition.values[1] || ""} onChange={(event) => setValueAt(1, event.target.value)} /></label>
                                </>
                            ) : (
                                <label><span>Amount</span><input className="rc-custom-value" placeholder="e.g. 30" value={condition.values[0] || ""} onChange={(event) => setValueAt(0, event.target.value)} /></label>
                            )}
                            <label>
                                <span>Unit</span>
                                <ValuePicker
                                    mode="select"
                                    options={unitOptions}
                                    placeholder={unitLabel}
                                    searchPlaceholder="Search Units..."
                                    onPick={(value) => updateCondition(ctx, condition.id, (current) => ({ ...current, differenceUnit: value as "minute" | "hour" | "day" }))}
                                />
                            </label>
                        </div>
                        <label className="rc-cb rc-minutes-toggle">
                            <input
                                type="checkbox"
                                checked={!!condition.absoluteMinutes}
                                onChange={(event) => updateCondition(ctx, condition.id, (current) => ({ ...current, absoluteMinutes: event.target.checked }))}
                            /> Ignore which time comes first
                        </label>
                        <p className="rc-hint">{unitHint}</p>
                    </div>
                )}
                {mode === "value" && condition.column && (
                    <div className="rc-condition-values">
                        {operator.valueMode === "multi" && (
                            <ValueMultiSelect
                                options={options}
                                values={condition.values}
                                onChange={(next) => updateCondition(ctx, condition.id, (current) => ({ ...current, values: next }))}
                                getLabel={(value) => lookupLabelForReportColumn(report, condition.column, value)}
                            />
                        )}
                        {operator.valueMode === "single" && (
                            <div className="rc-condition-value-entry">
                                <ValuePicker mode="select" options={options} placeholder={condition.values[0] ? lookupLabelForReportColumn(report, condition.column, condition.values[0]) : "Choose From List..."} onPick={(value) => updateCondition(ctx, condition.id, (current) => ({ ...current, values: value ? [value] : [] }))} />
                                <input className="rc-custom-value" placeholder={singleValuePlaceholder(selectedColumn)} value={values.includes(condition.values[0]) ? "" : condition.values[0] || ""} onChange={(event) => updateCondition(ctx, condition.id, (current) => ({ ...current, values: event.target.value ? [event.target.value] : [] }))} />
                            </div>
                        )}
                        {operator.valueMode === "range" && (
                            <div className="rc-condition-value-entry rc-range-entry">
                                <label><span>From</span><input className="rc-custom-value" placeholder={rangeBoundPlaceholder(selectedColumn)} value={condition.values[0] || ""} onChange={(event) => setValueAt(0, event.target.value)} /></label>
                                <label><span>To</span><input className="rc-custom-value" placeholder={rangeBoundPlaceholder(selectedColumn)} value={condition.values[1] || ""} onChange={(event) => setValueAt(1, event.target.value)} /></label>
                            </div>
                        )}
                        {operator.valueMode === "text" && <input className="rc-custom-value" placeholder="Text To Match" value={condition.text} onChange={(event) => updateCondition(ctx, condition.id, (current) => ({ ...current, text: event.target.value }))} />}
                    </div>
                )}
            </div>
        );
    }

    /**
     * "If blank, use <value>" - offered for any column whose type can be checked, with every
     * value operator except Is Blank / Is Not Blank. The input matches the column: a date
     * picker, Yes / No, the column's own list, or a number / text box. The value is checked
     * against the column's database type as it is typed (nullFallbackIssue); the server runs
     * the same check and refuses SQL with a value that does not fit.
     */
    function renderNullFallback(ctx: RuleTreeCtx, condition: RuleCondition, column?: DataColumn | null) {
        if (!condition.column || !nullFallbackSupported(condition, column)) return null;
        const report = ctx.scope;
        const kind = nullFallbackType(column).kind;
        const value = conditionBlankValue(condition);
        const enabled = value !== undefined;
        const label = column?.label || "this column";
        // Writing blankValue retires the legacy date-only blankDate for this condition.
        const setValue = (next: string | undefined) => updateCondition(ctx, condition.id, (current) => ({ ...current, blankValue: next, blankDate: undefined }));
        const defaultValue = kind === "date" ? DEFAULT_BLANK_DATE : kind === "bit" ? "0" : "";
        const listOptions = kind === "text" || kind === "integer" || kind === "decimal" ? lookupOptionsForReportColumn(report, condition.column) : [];
        const issue = enabled ? nullFallbackIssue(condition, column) : null;
        const fallback = enabled ? nullFallbackFor(condition, column) : null;
        const placeholder = kind === "integer" ? "Whole Number" : kind === "decimal" || kind === "float" ? "Number" : "Text (Leave Empty For Empty Text)";
        return (
            <div className="rc-condition-values">
                <label className="rc-cb">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(event) => setValue(event.target.checked ? defaultValue : undefined)}
                    /> If <strong>{label}</strong> is blank, use {kind === "date" ? "a date" : "a value"} instead
                </label>
                {enabled && kind === "date" && (
                    <ReportDatePicker value={(value || "").slice(0, 10)} placeholder="mm/dd/yyyy" onChange={(next) => setValue(next)} />
                )}
                {enabled && kind === "bit" && (
                    <ValuePicker
                        mode="select"
                        options={[{ id: "1", value: "1", name: "Yes" }, { id: "0", value: "0", name: "No" }]}
                        placeholder={fallback?.display || "Choose Yes Or No..."}
                        searchPlaceholder="Search..."
                        onPick={(next) => setValue(next)}
                    />
                )}
                {enabled && kind !== "date" && kind !== "bit" && (
                    <div className="rc-condition-value-entry">
                        {listOptions.length > 0 && (
                            <ValuePicker
                                mode="select"
                                options={listOptions}
                                placeholder={value ? lookupLabelForReportColumn(report, condition.column, value) : "Choose From List..."}
                                onPick={(next) => setValue(next)}
                            />
                        )}
                        <input
                            className={issue ? "rc-custom-value rc-invalid" : "rc-custom-value"}
                            placeholder={listOptions.length ? `Or Type a ${placeholder}` : placeholder}
                            value={listOptions.some((option) => option.value === value) ? "" : value || ""}
                            onChange={(event) => setValue(event.target.value)}
                        />
                    </div>
                )}
                {enabled && (
                    <p className="rc-hint">
                        {issue || (fallback ? `Rows with no ${label} are compared as ${fallback.display}.` : `Pick the value to use when ${label} is blank.`)}
                    </p>
                )}
            </div>
        );
    }

    /** Updates one leaf condition anywhere in the ctx's tree, however deeply nested. */
    function updateCondition(ctx: RuleTreeCtx, conditionId: string, updater: (condition: RuleCondition) => RuleCondition) {
        updateRuleTree(ctx, (nodes) => mapRuleNodes(nodes, conditionId, (node) => isRuleGroupNode(node) ? node : updater(node as RuleCondition)));
    }

    /** Placeholder hinting at the shape a single value should take for this column's type. */
    function singleValuePlaceholder(column?: DataColumn | null) {
        const category = columnDataTypeCategory(column);
        if (category === "numeric") return "Or Type a Number";
        if (category === "date") return "Or Type a Date (YYYY-MM-DD)";
        return "Or Type a Custom Value";
    }

    function rangeBoundPlaceholder(column?: DataColumn | null) {
        return columnDataTypeCategory(column) === "date" ? "YYYY-MM-DD" : "Number";
    }
    return renderRuleGroupBody(ctx, groupId, nodes, logic, depth);
}
